import { Link } from "react-router-dom";
import { ArrowRight, Check, MessageCircle, Phone, Baby, ShieldCheck, Trees, GraduationCap } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { PHONE_DISPLAY, PHONE_TEL, bookingHref, waLink } from "@/lib/site";

const WA_KIDS = waLink("Merhaba FX Motor Sporları, çocuk eğitimi 5 derslik paket (25.000 TL) hakkında bilgi almak istiyorum. Çocuğumun yaşı: ");

const LESSONS = [
  { t: "Tanışma ve ekipman", d: "Motorla ve ekipmanla tanışma, güvenlik kuralları, motor üzerinde doğru duruş." },
  { t: "Gaz ve fren kontrolü", d: "Kontrollü kalkış, yumuşak gaz, güvenli duruş ve fren alışkanlığı." },
  { t: "Denge ve dönüşler", d: "Düşük hızda denge, geniş ve dar dönüşler, bakış yönü." },
  { t: "Ayakta sürüş ve engeller", d: "Ayakta sürüş pozisyonu, küçük engeller ve zemin değişimleri." },
  { t: "Parkurda uygulama", d: "Öğrenilenleri parkurda birleştirme ve eğitmenle değerlendirme." },
];

const WHY = [
  { icon: ShieldCheck, t: "Önce güvenlik", d: "Her ders güvenlik kuralları ve ekipman kontrolüyle başlar." },
  { icon: GraduationCap, t: "Adım adım", d: "Beş derste temelden parkura, çocuğun hızına göre ilerleme." },
  { icon: Trees, t: "Doğanın içinde", d: "Ekrandan uzak, Ömerli'nin ormanında açık havada bir gün." },
  { icon: Baby, t: "Özgüven kazanır", d: "Kontrolü öğrendikçe cesaret ve özgüven de gelir." },
];

const PackageCard = () => (
  <div data-testid="kids-package-card" className="relative border border-white/15 bg-black p-8 md:p-10">
    <span className="absolute -top-3 left-8 -skew-x-12 bg-brand px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-white"><span className="block skew-x-12">Çocuk Eğitim Paketi</span></span>
    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500">5 ders · toplam</p>
    <p data-testid="kids-price" className="mt-2 font-display text-7xl leading-none text-white md:text-8xl">25.000 TL</p>
    <p className="mt-3 text-sm text-neutral-400">5 derslik eğitim paketinin toplam bedelidir.</p>
    <ul className="mt-8 space-y-3 border-t border-white/10 pt-8">
      {LESSONS.map((l, i) => <li key={l.t} className="flex gap-3 text-neutral-200"><Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" />{i + 1}. Ders — {l.t}</li>)}
    </ul>
    <div className="mt-10 flex flex-col gap-3">
      <Link to={bookingHref("cocuk-egitim")} data-testid="kids-booking-btn" className="group flex items-center justify-center gap-3 bg-brand px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-brand">İlk derse yer ayırt <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></Link>
      <a href={WA_KIDS} target="_blank" rel="noopener noreferrer" data-testid="kids-whatsapp-btn" className="flex items-center justify-center gap-3 border border-white/20 px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:border-white"><MessageCircle className="h-5 w-5" /> WhatsApp'tan sor</a>
    </div>
  </div>
);

export const KidsPage = () => (
  <>
    <PageHero eyebrow="Aileler için" title="Çocuk Eğitim" intro="Motor sevgisi erken başlar. Beş derslik pakette çocuğunuz motosikleti güvenle tanır, kontrolü öğrenir ve parkurda ilk sürüşünü yapar." img="/ig/ig09.jpg" alt="FX Motors finish garajı önünde motosiklet" crumbs={[{ label: "Eğitimler", to: "/egitimlerimiz" }, { label: "Çocuk Eğitim" }]} />

    <section className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 py-24 md:px-8 md:py-32 lg:grid-cols-[1.2fr_1fr]">
      <Reveal>
        <Eyebrow>5 derslik paket</Eyebrow>
        <h2 className="font-display text-5xl uppercase leading-none text-white md:text-7xl">Güvenle başla, keyifle sür.</h2>
        <div className="mt-8 space-y-5 text-base leading-relaxed text-neutral-400 md:text-lg">
          <p>Çocuk eğitimimiz; motosikleti ilk kez tanıyacak ya da temelini sağlamlaştırmak isteyen çocuklar için beş dersten oluşan bir pakettir. Her ders bir öncekinin üzerine kurulur.</p>
          <p>İlk derste motorla ve ekipmanla tanışılır, son derste öğrenilenler parkurda birleştirilir. Ders içerikleri çocuğun yaşına ve seviyesine göre eğitmenimiz tarafından uyarlanır.</p>
          <p>Çocuğunuzun yaşı ve deneyimi için bize yazın; size uygun gün ve saati birlikte planlayalım.</p>
        </div>
        <div className="mt-12 space-y-3" data-testid="kids-lessons">
          {LESSONS.map((l, i) => (
            <div key={l.t} className="flex gap-6 border border-white/10 p-6">
              <span className="font-display text-5xl leading-none text-brand">0{i + 1}</span>
              <div>
                <h3 className="font-display text-2xl uppercase text-white md:text-3xl">{l.t}</h3>
                <p className="mt-1 text-neutral-400">{l.d}</p>
              </div>
            </div>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.1} className="lg:sticky lg:top-28 lg:self-start">
        <PackageCard />
      </Reveal>
    </section>

    <section className="border-t border-white/10 bg-surface">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-8">
        <Reveal className="mb-14">
          <Eyebrow>Neden FX</Eyebrow>
          <h2 className="font-display text-5xl uppercase leading-none text-white md:text-6xl">Aileler neden bizi seçiyor?</h2>
        </Reveal>
        <div className="grid grid-cols-1 border-l border-t border-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <Reveal key={w.t} delay={i * 0.08} className="border-b border-r border-white/10 p-8">
              <w.icon className="h-10 w-10 text-brand" strokeWidth={1.5} />
              <h3 className="mt-8 font-display text-3xl uppercase leading-tight text-white">{w.t}</h3>
              <p className="mt-3 leading-relaxed text-neutral-400">{w.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="kids-cta" className="bg-brand">
      <div className="mx-auto flex max-w-7xl flex-col justify-between gap-8 px-5 py-16 md:flex-row md:items-center md:px-8 md:py-20">
        <div>
          <h2 className="font-display text-5xl uppercase leading-none text-white md:text-6xl">İlk dersi planlayalım.</h2>
          <p className="mt-3 max-w-xl text-white/90">5 ders, toplam 25.000 TL. Bilgi için: {PHONE_DISPLAY}</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Link to={bookingHref("cocuk-egitim")} data-testid="kids-cta-booking-btn" className="flex items-center justify-center gap-3 bg-black px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black">Rezervasyon yap <ArrowRight className="h-5 w-5" /></Link>
          <a href={PHONE_TEL} data-testid="kids-cta-call-btn" className="flex items-center justify-center gap-3 border border-white px-8 py-4 font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-brand"><Phone className="h-5 w-5" /> Ara</a>
        </div>
      </div>
    </section>
  </>
);
